<template>
  <div>
    <v-row>
      <v-col :cols="noUnit || addressOnly ? null : 8" class="py-0">
        <anchor-text-field
          ref="addressField"
          id="address-form-street-input"
          :placeholder="placeholder"
          :label="addressLabel"
          :disabled="disabled"
          :rules="rules.address"
          v-model="value[attrNames.address]"
        />
      </v-col>
      <v-col v-if="!noUnit && !addressOnly" class="py-0">
        <anchor-text-field
          id="address-form-unit-input"
          label="Unit"
          :disabled="disabled"
          :rules="rules.unit"
          v-model="value[attrNames.unit]"
        />
      </v-col>
    </v-row>
    <v-row v-if="!addressOnly">
      <v-col :cols="cols.city" class="py-0">
        <anchor-text-field
          id="address-form-city-input"
          label="City"
          :disabled="disabled"
          :rules="rules.city"
          v-model="value[attrNames.city]"
        />
      </v-col>
      <v-col :cols="cols.state" class="py-0">
        <anchor-select
          id="address-form-state-select"
          label="State"
          :disabled="disabled"
          :items="options.state"
          :rules="rules.state"
          v-model="value[attrNames.state]"
        />
      </v-col>
      <v-col :cols="cols.zipCode" class="py-0">
        <anchor-text-field
          id="address-form-zip-input"
          label="Zip Code"
          :disabled="disabled"
          :rules="rules.zipCode"
          v-model="value[attrNames.zipCode]"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import RULES from '../../../resources/js/address/rules';
import OPTIONS from '../../../resources/js/address/options';

export default {
  name: 'AnchorAddressForm',
  props: {
    addressLabel: {
      type: String,
      default: 'Address',
    },
    prefix: String,
    addressOnly: Boolean,
    autocomplete: Boolean,
    aligned: Boolean,
    disabled: Boolean,
    noUnit: {
      type: Boolean,
      default: false,
    },
    validate: {
      type: Boolean,
      default: true,
    },
    value: Object,
  },
  data: () => ({
    options: OPTIONS,
    input: null,
  }),
  computed: {
    google: () => window.google,
    shouldSetAutocomplete: vm => vm.google && vm.autocomplete,
    placeholder: vm => (vm.shouldSetAutocomplete ? 'Enter a location' : null),
    rules: vm => (vm.validate ? RULES.required : RULES.optional),
    cols: vm => ({
      city: vm.aligned ? 4 : 6,
      state: vm.aligned ? 4 : 3,
      zipCode: vm.aligned ? 4 : 3,
    }),
    attrNames: vm => {
      const names = ['address', 'unit', 'city', 'state', 'zipCode'];
      const map = {};
      names.forEach(name => {
        map[name] = vm.prefix ? `${vm.prefix}${vm.$options.filters.title(name)}` : name;
      });
      return map;
    },
  },
  methods: {
    loadGoogleAutocomplete() {
      if (this.shouldSetAutocomplete) {
        this.setAutocomplete();
      } else {
        this.unsetAutocomplete();
      }
    },
    unsetAutocomplete() {
      try {
        window.google.maps.event.clearInstanceListeners(this.input);
      } catch (_) { /* Ignore if global google object has not been initialized */ }

      if (this.input) {
        this.input.classList.remove('gm-err-autocomplete');
        this.input.removeAttribute('disabled');
        this.input.removeAttribute('style');
        this.input.removeAttribute('placeholder');
        this.input = null;
      }
    },
    setAutocomplete() {
      this.input = this.$refs.addressField.$el.getElementsByTagName('input')[0];
      const options = {
        componentRestrictions: { country: 'us' },
      };
      const autocomplete = new window.google.maps.places.Autocomplete(this.input, options);
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!(place && place.address_components)) { return; }

        const addressFound = place.address_components;

        function resolveFieldValue(name, longName) {
          const fieldSearch = addressFound.filter(e => e.types[0] === name);
          if (fieldSearch.length) {
            /* eslint no-param-reassign: ["error", { "props": false }] */
            return longName ? fieldSearch[0].long_name : fieldSearch[0].short_name;
          }

          return '';
        }

        const addressNumber = resolveFieldValue('street_number', true);
        const addressRoute = resolveFieldValue('route', false);
        if (addressNumber !== '' && addressRoute !== '') {
          const address = `${addressNumber} ${addressRoute}`.trim();
          const city = resolveFieldValue('locality', true);
          const state = resolveFieldValue('administrative_area_level_1', false);
          const zipCode = resolveFieldValue('postal_code', true);

          if (this.addressOnly) {
            this.$set(this.value, this.attrNames.address, `${address}, ${city}, ${state} ${zipCode}`);
          } else {
            this.$set(this.value, this.attrNames.address, address);
            this.$set(this.value, this.attrNames.city, city);
            this.$set(this.value, this.attrNames.state, state);
            this.$set(this.value, this.attrNames.zipCode, zipCode);
            this.$set(this.value, this.attrNames.unit, null);
          }
        }
      });
    },
  },
  mounted() {
    this.loadGoogleAutocomplete();
  },
  watch: {
    autocomplete() {
      this.loadGoogleAutocomplete();
    },
    google() {
      this.loadGoogleAutocomplete();
    },
  },
};
</script>
